/**
 * Tiny REST helper for the platform-service endpoints (currently just
 * the asset upload endpoint, which can't go through `gql()` because
 * graphql-request doesn't do multipart).
 *
 * Reuses the auth store's access token. We don't proactively refresh
 * here — uploads are short-lived and the GraphQL client refreshes
 * shortly before/after this in the typical flow. If a 401 comes back
 * we surface it; the global auth guard will boot the user to login.
 */

import { useAuthStore } from '../features/auth/authStore';

const PLATFORM_SERVICE_BASE =
  import.meta.env.VITE_PLATFORM_SERVICE_URL ?? 'http://localhost:3010';

export async function postMultipart<T>(
  path: string,
  formData: FormData,
): Promise<T> {
  const token = useAuthStore.getState().accessToken;
  const res = await fetch(`${PLATFORM_SERVICE_BASE}${path}`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });
  if (!res.ok) {
    let detail = `${res.status} ${res.statusText}`;
    try {
      const body = await res.json();
      if (body?.message) detail = body.message;
      else if (body?.error) detail = body.error;
    } catch {
      /* ignore — keep the status text */
    }
    throw new Error(detail);
  }
  return (await res.json()) as T;
}
